import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, Package, Trash2, Edit, Store, Loader2, Eye, Phone, Mail, 
  MapPin, Building, FileText, ExternalLink, MessageSquare, Plus, Clock, 
  PhoneCall, Calendar, AlertCircle, Send, UserCheck, TrendingUp, LogIn
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { admin } from '@/lib/api';
import { useCurrency } from '@/lib/currency-context';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { getMembershipTierLabel, getMembershipTierStyle, getMembershipStatusLabel } from '@/lib/membership-utils';
import SellerCountryBadge from '@/components/admin/SellerCountryBadge';

/** address alanı string veya {full_name, address_line1, ...} objesi olabilir */
const formatUserAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  if (typeof address !== 'object') return String(address);
  const lines = [
    address.full_name,
    address.address_line1 || address.address,
    address.address_line2,
    [address.city, address.state, address.postal_code].filter(Boolean).join(', '),
    address.country,
    address.phone,
  ].filter(Boolean);
  return lines.join('\n');
};

const UserDetailDialog = ({
  open,
  onOpenChange,
  selectedUser,
  onUserUpdated,
  hasPermission,
  onImpersonate
}) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'tr').split('-')[0];
  const { formatPrice } = useCurrency();
  
  // Local state
  const [userNotes, setUserNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteType, setNewNoteType] = useState('general');
  const [addingNote, setAddingNote] = useState(false);
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  
  const [userProducts, setUserProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showProductsSection, setShowProductsSection] = useState(false);

  const fetchUserNotes = useCallback(async (userId) => {
    setLoadingNotes(true);
    try {
      const response = await admin.getUserNotes(userId);
      setUserNotes(response.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      toast.error(t('admin.notes.loadError') || 'Notlar yüklenemedi');
    } finally {
      setLoadingNotes(false);
    }
  }, [t]);

  useEffect(() => {
    if (open && selectedUser) {
      setUserNotes([]);
      setNewNoteContent('');
      setNewNoteType('general');
      setIsEditMode(false);
      setEditFormData({});
      setUserProducts([]);
      setShowProductsSection(false);
      fetchUserNotes(selectedUser.id);
    }
  }, [open, selectedUser?.id, fetchUserNotes]);

  const fetchUserProducts = async (userId) => {
    setLoadingProducts(true);
    try {
      const response = await admin.getUserProducts(userId);
      setUserProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error(t('admin.products.loadError') || 'Ürünler yüklenemedi');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleEnterEditMode = () => {
    const addr = selectedUser?.address;
    const addressObj = addr && typeof addr === 'object' ? addr : null;
    setIsEditMode(true);
    setEditFormData({
      name: selectedUser?.name || '',
      email: selectedUser?.email || '',
      phone: selectedUser?.phone || '',
      address: formatUserAddress(addr),
      city: selectedUser?.city || addressObj?.city || '',
      district: selectedUser?.district || addressObj?.state || '',
      country: selectedUser?.country || addressObj?.country || '',
      email_verified: selectedUser?.email_verified || false,
      is_active: selectedUser?.is_active !== false, // Default true if not set
      seller_status: selectedUser?.seller_status || '',
      seller_type: selectedUser?.seller_type || '',
      seller_category: selectedUser?.seller_category || '',
      tax_number: selectedUser?.tax_number || '',
      company_name: selectedUser?.company_name || '',
      company_tax_number: selectedUser?.company_tax_number || '',
      membership_tier: selectedUser?.membership_tier || 'bronze',
      membership_status: selectedUser?.membership_status || 'active',
      role: selectedUser?.role || 'user',
    });
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    setSavingUser(true);
    try {
      const response = await admin.updateUser(selectedUser.id, editFormData);
      setIsEditMode(false);
      toast.success(t('admin.userUpdated') || 'Kullanıcı güncellendi');
      if (onUserUpdated) onUserUpdated(response.data.user);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || t('admin.updateError') || 'Güncelleme başarısız');
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await admin.resetUserPassword(selectedUser.id);
      toast.success(`${t('admin.passwordReset') || 'Şifre sıfırlandı'}: ${response.data.temporary_password}`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(t('admin.resetError') || 'Şifre sıfırlanamadı');
    }
  };

  const handleToggleProducts = () => {
    if (!showProductsSection && selectedUser) {
      fetchUserProducts(selectedUser.id);
    }
    setShowProductsSection(!showProductsSection);
  };

  const handleUpdateProduct = async (productId, updates) => {
    try {
      await admin.updateProduct(productId, updates);
      toast.success(t('admin.productUpdated') || 'Ürün güncellendi');
      if (selectedUser) fetchUserProducts(selectedUser.id);
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error(t('admin.updateError') || 'Güncelleme başarısız');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(t('admin.confirmDeleteProduct') || 'Bu ürünü silmek istediğinize emin misiniz?')) return;
    
    try {
      await admin.deleteProduct(productId);
      toast.success(t('admin.productDeleted') || 'Ürün silindi');
      if (selectedUser) fetchUserProducts(selectedUser.id);
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error(t('admin.deleteError') || 'Silme başarısız');
    }
  };

  const handleAddNote = async () => {
    if (!selectedUser || !newNoteContent.trim()) return;
    
    setAddingNote(true);
    try {
      await admin.addUserNote(selectedUser.id, {
        content: newNoteContent.trim(),
        note_type: newNoteType
      });
      toast.success(t('admin.notes.added') || 'Not eklendi');
      setNewNoteContent('');
      setNewNoteType('general');
      fetchUserNotes(selectedUser.id);
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error(t('admin.notes.addError') || 'Not eklenemedi');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedUser) return;
    
    try {
      await admin.deleteUserNote(selectedUser.id, noteId);
      toast.success(t('admin.notes.deleted') || 'Not silindi');
      fetchUserNotes(selectedUser.id);
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error(t('admin.notes.deleteError') || 'Not silinemedi');
    }
  };

  const getNoteTypeIcon = (type) => {
    switch (type) {
      case 'call': return <PhoneCall className="w-3 h-3" />;
      case 'meeting': return <Calendar className="w-3 h-3" />;
      case 'complaint': return <AlertCircle className="w-3 h-3" />;
      default: return <MessageSquare className="w-3 h-3" />;
    }
  };

  const getNoteTypeLabel = (type) => {
    switch (type) {
      case 'call': return t('admin.notes.typeCall') || 'Telefon';
      case 'meeting': return t('admin.notes.typeMeeting') || 'Toplantı';
      case 'complaint': return t('admin.notes.typeComplaint') || 'Şikayet';
      default: return t('admin.notes.typeGeneral') || 'Genel';
    }
  };

  const getNoteTypeBadgeColor = (type) => {
    switch (type) {
      case 'call': return 'bg-blue-100 text-blue-700';
      case 'meeting': return 'bg-purple-100 text-purple-700';
      case 'complaint': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    onOpenChange(false);
  };

  if (!selectedUser) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold text-lg">
                  {selectedUser?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p>{isEditMode ? (editFormData.name || selectedUser?.name) : selectedUser?.name}</p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="secondary" className="capitalize">{selectedUser?.role}</Badge>
                  {selectedUser?.role === 'seller' && selectedUser?.seller_status && (
                    <Badge 
                      variant={selectedUser.seller_status === 'approved' ? 'default' : selectedUser.seller_status === 'pending' ? 'secondary' : 'destructive'}
                    >
                      {selectedUser.seller_status}
                    </Badge>
                  )}
                  {selectedUser?.membership_tier && (
                    <Badge
                      variant="outline"
                      className={`border ${getMembershipTierStyle(selectedUser.membership_tier)}`}
                    >
                      {getMembershipTierLabel(selectedUser.membership_tier, (i18n.language || 'tr').split('-')[0])}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {!isEditMode ? (
                hasPermission('users.edit') && (
                  <Button variant="outline" size="sm" onClick={handleEnterEditMode}>
                    <Edit className="w-4 h-4 mr-1" />
                    {t('common.edit') || 'Düzenle'}
                  </Button>
                )
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setIsEditMode(false)}>
                    {t('common.cancel') || 'İptal'}
                  </Button>
                  <Button size="sm" onClick={handleSaveUser} disabled={savingUser}>
                    {savingUser ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    {t('common.save') || 'Kaydet'}
                  </Button>
                </>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>
            {isEditMode ? (t('admin.editMode') || 'Düzenleme modu - değişiklikleri kaydedin') : (t('admin.userDetails') || 'Kullanıcı detaylı bilgileri')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Basic Information */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t('admin.sellers.basicInfo') || 'Temel Bilgiler'}
            </h4>
            {isEditMode ? (
              <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                <div className="space-y-2">
                  <Label>{t('auth.name') || 'İsim'}</Label>
                  <Input
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.email') || 'E-posta'}</Label>
                  <Input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.sellers.phone') || 'Telefon'}</Label>
                  <Input
                    value={editFormData.phone || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.role') || 'Rol'}</Label>
                  <Select 
                    value={editFormData.role || 'user'} 
                    onValueChange={(val) => setEditFormData(prev => ({ ...prev, role: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">{t('admin.roleUser') || 'Kullanıcı'}</SelectItem>
                      <SelectItem value="seller">{t('admin.roleSeller') || 'Satıcı'}</SelectItem>
                      <SelectItem value="admin">{t('admin.roleAdmin') || 'Admin'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-background rounded">
                  <Label>{t('admin.emailVerified') || 'E-posta Doğrulandı'}</Label>
                  <Switch
                    checked={editFormData.email_verified || false}
                    onCheckedChange={(checked) => setEditFormData(prev => ({ ...prev, email_verified: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-background rounded">
                  <div>
                    <Label>{t('admin.accountActive') || 'Hesap Aktif'}</Label>
                    <p className="text-xs text-muted-foreground">{t('admin.accountActiveDesc') || 'Pasif hesaplar giriş yapamaz'}</p>
                  </div>
                  <Switch
                    checked={editFormData.is_active !== false}
                    onCheckedChange={(checked) => setEditFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('auth.email') || 'E-posta'}</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.sellers.phone') || 'Telefon'}</p>
                    <p className="font-medium">{selectedUser.phone || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.role') || 'Rol'}</p>
                    <p className="font-medium capitalize">{selectedUser.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.joined') || 'Kayıt Tarihi'}</p>
                    <p className="font-medium">{selectedUser.created_at ? formatDate(selectedUser.created_at) : '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.emailVerified') || 'E-posta Doğrulandı'}</p>
                    <Badge variant={selectedUser.email_verified ? 'default' : 'secondary'}>
                      {selectedUser.email_verified ? (t('common.yes') || 'Evet') : (t('common.no') || 'Hayır')}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.accountStatus') || 'Hesap Durumu'}</p>
                    <Badge variant={selectedUser.is_active !== false ? 'default' : 'destructive'}>
                      {selectedUser.is_active !== false ? (t('admin.active') || 'Aktif') : (t('admin.inactive') || 'Pasif')}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Membership Information */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('admin.membershipInfo') || 'Üyelik Bilgileri'}
            </h4>
            {isEditMode ? (
              <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                <div className="space-y-2">
                  <Label>{t('admin.membershipTier') || 'Üyelik Seviyesi'}</Label>
                  <Select 
                    value={editFormData.membership_tier || 'bronze'} 
                    onValueChange={(val) => setEditFormData(prev => ({ ...prev, membership_tier: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bronze">{getMembershipTierLabel('bronze', locale)}</SelectItem>
                      <SelectItem value="silver">{getMembershipTierLabel('silver', locale)}</SelectItem>
                      <SelectItem value="gold">{getMembershipTierLabel('gold', locale)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.membershipStatus') || 'Üyelik Durumu'}</Label>
                  <Select 
                    value={editFormData.membership_status || 'active'} 
                    onValueChange={(val) => setEditFormData(prev => ({ ...prev, membership_status: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t('admin.statusActive') || 'Aktif'}</SelectItem>
                      <SelectItem value="expired">{t('admin.statusExpired') || 'Süresi Dolmuş'}</SelectItem>
                      <SelectItem value="cancelled">{t('admin.statusCancelled') || 'İptal Edilmiş'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">{t('admin.membershipTier')}</p>
                  {selectedUser.membership_tier ? (
                    <Badge
                      variant="outline"
                      className={`mt-1 border ${getMembershipTierStyle(selectedUser.membership_tier)}`}
                    >
                      {getMembershipTierLabel(selectedUser.membership_tier, locale)}
                    </Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">{t('admin.noMembership')}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('admin.membershipStatus')}</p>
                  <Badge 
                    variant={selectedUser.membership_status === 'active' ? 'default' : 'secondary'}
                    className="mt-1"
                  >
                    {getMembershipStatusLabel(selectedUser.membership_status, t)}
                  </Badge>
                </div>
                {selectedUser.membership_billing_period && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('membership.monthly')}/{t('membership.yearly')}</p>
                    <p className="text-sm font-medium mt-1 capitalize">
                      {selectedUser.membership_billing_period === 'yearly' ? t('membership.yearly') : t('membership.monthly')}
                    </p>
                  </div>
                )}
                {selectedUser.membership_end_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('admin.membershipEndDate', 'Bitiş tarihi')}</p>
                    <p className="text-sm font-medium mt-1">{formatDate(selectedUser.membership_end_date)}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Seller Specific Information */}
          {selectedUser.role === 'seller' && (
            <>
              {/* Seller Type & Category */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Store className="w-4 h-4" />
                  {t('admin.sellerInfo') || 'Satıcı Bilgileri'}
                </h4>
                {isEditMode ? (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                    <div className="space-y-2">
                      <Label>{t('admin.sellers.type') || 'Tür'}</Label>
                      <Select 
                        value={editFormData.seller_type || 'individual'} 
                        onValueChange={(val) => setEditFormData(prev => ({ ...prev, seller_type: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">{t('admin.sellers.typeIndividual') || 'Bireysel'}</SelectItem>
                          <SelectItem value="corporate">{t('admin.sellers.typeCompany') || 'Kurumsal'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('admin.sellers.category') || 'Kategori'}</Label>
                      <Select
                        value={editFormData.seller_category || undefined}
                        onValueChange={(val) => setEditFormData(prev => ({ ...prev, seller_category: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Kategori seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            ['products', 'Ürünler'],
                            ['supermarket', 'Süpermarket'],
                            ['restaurants', 'Restoran'],
                            ['cars', 'Araç Kiralama'],
                            ['hotels', 'Otel'],
                            ['flights', 'Uçak'],
                            ['taxi-limousine', 'Taksi & Limuzin'],
                            ['auto-parts', 'Yedek Parça'],
                            ['tires', 'Lastik'],
                          ].map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                          {editFormData.seller_category && ![
                            'products', 'supermarket', 'restaurants', 'cars', 'hotels', 'flights',
                            'taxi-limousine', 'auto-parts', 'tires',
                          ].includes(editFormData.seller_category) && (
                            <SelectItem value={editFormData.seller_category}>{editFormData.seller_category}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('admin.sellers.status') || 'Durum'}</Label>
                      <Select 
                        value={editFormData.seller_status || 'pending'} 
                        onValueChange={(val) => setEditFormData(prev => ({ ...prev, seller_status: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">{t('admin.sellers.statusPending') || 'Beklemede'}</SelectItem>
                          <SelectItem value="approved">{t('admin.sellers.statusApproved') || 'Onaylı'}</SelectItem>
                          <SelectItem value="rejected">{t('admin.sellers.statusRejected') || 'Reddedildi'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('admin.sellers.taxNumber') || 'Vergi Numarası'}</Label>
                      <Input
                        value={editFormData.tax_number || ''}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, tax_number: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('admin.sellers.country') || 'Ülke'}</Label>
                      <Input
                        value={editFormData.country || ''}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, country: e.target.value }))}
                        placeholder="DE (Almanya), TR, US…"
                      />
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.countryHint')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.type') || 'Tür'}</p>
                      <p className="font-medium capitalize">
                        {selectedUser.seller_type === 'corporate' 
                          ? (t('admin.sellers.typeCompany') || 'Kurumsal') 
                          : (t('admin.sellers.typeIndividual') || 'Bireysel')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.category') || 'Kategori'}</p>
                      <p className="font-medium capitalize">{selectedUser.seller_category || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.status') || 'Durum'}</p>
                      <Badge 
                        variant={selectedUser.seller_status === 'approved' ? 'default' : selectedUser.seller_status === 'pending' ? 'secondary' : 'destructive'}
                      >
                        {selectedUser.seller_status === 'approved' ? (t('admin.sellers.statusApproved') || 'Onaylı') :
                         selectedUser.seller_status === 'pending' ? (t('admin.sellers.statusPending') || 'Beklemede') :
                         (t('admin.sellers.statusRejected') || 'Reddedildi')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.country') || 'Ülke'}</p>
                      <SellerCountryBadge country={selectedUser.country} />
                    </div>
                    {selectedUser.tax_number && (
                      <div>
                        <p className="text-xs text-muted-foreground">{t('admin.sellers.taxNumber') || 'Vergi Numarası'}</p>
                        <p className="font-medium">{selectedUser.tax_number}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Company Information (if corporate) */}
              {selectedUser.seller_type === 'corporate' && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    {t('admin.sellers.companyInfo') || 'Şirket Bilgileri'}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.companyName') || 'Şirket Adı'}</p>
                      <p className="font-medium">{selectedUser.company_name || '-'}</p>
                    </div>
                    {selectedUser.company_tax_number && (
                      <div>
                        <p className="text-xs text-muted-foreground">{t('admin.sellers.taxNumber') || 'Vergi Numarası'}</p>
                        <p className="font-medium">{selectedUser.company_tax_number}</p>
                      </div>
                    )}
                    {selectedUser.trade_register_number && (
                      <div>
                        <p className="text-xs text-muted-foreground">{t('admin.tradeRegisterNumber') || 'Ticaret Sicil No'}</p>
                        <p className="font-medium">{selectedUser.trade_register_number}</p>
                      </div>
                    )}
                    {selectedUser.authorized_person && (
                      <div>
                        <p className="text-xs text-muted-foreground">{t('admin.authorizedPerson') || 'Yetkili Kişi'}</p>
                        <p className="font-medium">{selectedUser.authorized_person}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Address Information — address can be string or structured object */}
          {(selectedUser.address || selectedUser.district || selectedUser.city || selectedUser.country) && (() => {
            const addr = selectedUser.address;
            const isAddrObj = addr && typeof addr === 'object';
            const displayAddress = formatUserAddress(addr);
            const district = selectedUser.district || (isAddrObj ? addr.state : '');
            const city = selectedUser.city || (isAddrObj ? addr.city : '');
            const country = selectedUser.country || (isAddrObj ? addr.country : '');
            // Structured address already includes city/state/country in displayAddress
            const showSeparateFields = !isAddrObj;

            return (
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('admin.sellers.address') || 'Adres Bilgileri'}
                </h4>
                <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-lg">
                  {displayAddress && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.address') || 'Adres'}</p>
                      <p className="font-medium whitespace-pre-line">{displayAddress}</p>
                    </div>
                  )}
                  {showSeparateFields && district && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.district') || 'İlçe'}</p>
                      <p className="font-medium capitalize">{district}</p>
                    </div>
                  )}
                  {showSeparateFields && city && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.city') || 'Şehir'}</p>
                      <p className="font-medium capitalize">{city}</p>
                    </div>
                  )}
                  {showSeparateFields && country && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('admin.sellers.country') || 'Ülke'}</p>
                      <p className="font-medium capitalize">{country}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Documents (for sellers) */}
          {selectedUser.role === 'seller' && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('admin.sellers.documents') || 'Belgeler'}
              </h4>
              <div className="p-4 bg-secondary/30 rounded-lg">
                {(selectedUser.id_document_url || selectedUser.trade_register_doc_url || selectedUser.authorized_person_id_url) ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {selectedUser.id_document_url && (
                      <a 
                        href={selectedUser.id_document_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-background rounded-lg border hover:border-primary transition-colors"
                      >
                        <FileText className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('admin.sellers.idDocument') || 'Kimlik Belgesi'}</span>
                        <ExternalLink className="w-3 h-3 ml-auto" />
                      </a>
                    )}
                    {selectedUser.trade_register_doc_url && (
                      <a 
                        href={selectedUser.trade_register_doc_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-background rounded-lg border hover:border-primary transition-colors"
                      >
                        <FileText className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('admin.sellers.tradeRegister') || 'Ticari Sicil'}</span>
                        <ExternalLink className="w-3 h-3 ml-auto" />
                      </a>
                    )}
                    {selectedUser.authorized_person_id_url && (
                      <a 
                        href={selectedUser.authorized_person_id_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-background rounded-lg border hover:border-primary transition-colors"
                      >
                        <FileText className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('admin.sellers.authorizedPersonId') || 'Yetkili Kimliği'}</span>
                        <ExternalLink className="w-3 h-3 ml-auto" />
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('admin.sellers.noDocuments') || 'Yüklenmiş belge bulunmuyor.'}</p>
                )}
              </div>
            </div>
          )}

          {/* Rejection Reason (if rejected) */}
          {selectedUser.seller_status === 'rejected' && selectedUser.rejection_reason && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-destructive uppercase tracking-wide">
                {t('admin.sellers.rejectionReason') || 'Red Nedeni'}
              </h4>
              <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <p className="text-sm">{selectedUser.rejection_reason}</p>
              </div>
            </div>
          )}

          {/* Admin Actions */}
          <div className="space-y-3 border-t pt-6">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              {t('admin.adminActions') || 'Yönetici İşlemleri'}
            </h4>
            <div className="flex flex-wrap gap-2 p-4 bg-secondary/30 rounded-lg">
              {hasPermission('users.impersonate') && (
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => onImpersonate && onImpersonate(selectedUser)}
                  className="bg-primary hover:bg-primary/90"
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  {t('admin.loginAsUser') || 'Hesaba Giriş Yap'}
                </Button>
              )}
              {hasPermission('users.edit') && (
                <Button variant="outline" size="sm" onClick={handleResetPassword}>
                  <Edit className="w-4 h-4 mr-1" />
                  {t('admin.resetPassword') || 'Şifre Sıfırla'}
                </Button>
              )}
              {selectedUser.role === 'seller' && hasPermission('products.view') && (
                <Button variant="outline" size="sm" onClick={handleToggleProducts}>
                  <Package className="w-4 h-4 mr-1" />
                  {showProductsSection 
                    ? (t('admin.hideProducts') || 'Ürünleri Gizle') 
                    : (t('admin.showProducts') || 'Ürünleri Göster')}
                </Button>
              )}
            </div>
          </div>

          {/* Seller Products Section */}
          {selectedUser.role === 'seller' && showProductsSection && (
            <div className="space-y-3 border-t pt-6">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Package className="w-4 h-4" />
                {t('admin.sellerProducts') || 'Satıcı Ürünleri'} ({userProducts.length})
              </h4>
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : userProducts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground p-4 bg-secondary/30 rounded-lg">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('admin.noProducts') || 'Bu satıcının henüz ürünü yok.'}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userProducts.map((product) => (
                    <div 
                      key={product.id} 
                      className="p-3 bg-secondary/30 rounded-lg flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        {product.images?.[0] && (
                          <img 
                            src={product.images[0]} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{formatPrice(product.price, product.currency || 'EUR')}</span>
                            <span>•</span>
                            <span>{t('seller.stock') || 'Stok'}: {product.stock}</span>
                            <span>•</span>
                            <Badge variant={product.is_active !== false ? 'default' : 'secondary'} className="text-xs">
                              {product.is_active !== false ? (t('admin.active') || 'Aktif') : (t('admin.inactive') || 'Pasif')}
                            </Badge>
                            {product.made_in_turkey && (
                              <img 
                                src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
                                alt="Made in Türkiye"
                                className="w-5 h-5 object-contain"
                                title="Made in Türkiye"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Made in Türkiye Toggle */}
                        <div className="flex items-center gap-1 mr-2" title="Made in Türkiye Rozeti">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
                            alt=""
                            className={`w-4 h-4 object-contain transition-opacity ${product.made_in_turkey ? 'opacity-100' : 'opacity-30'}`}
                          />
                          <Switch
                            checked={product.made_in_turkey || false}
                            onCheckedChange={(checked) => handleUpdateProduct(product.id, { made_in_turkey: checked })}
                            className="scale-75"
                          />
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            const newStatus = product.is_active !== false ? false : true;
                            handleUpdateProduct(product.id, { is_active: newStatus });
                          }}
                          title={product.is_active !== false ? 'Pasif Yap' : 'Aktif Yap'}
                        >
                          {product.is_active !== false ? (
                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                          ) : (
                            <UserCheck className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes Section */}
          <div className="space-y-3 border-t pt-6">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {t('admin.notes.title') || 'Notlar ve Görüşmeler'}
            </h4>
            
            {/* Add Note Form */}
            <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
              <div className="flex gap-2 items-center">
                <Select value={newNoteType} onValueChange={setNewNoteType}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        {t('admin.notes.typeGeneral') || 'Genel'}
                      </div>
                    </SelectItem>
                    <SelectItem value="call">
                      <div className="flex items-center gap-2">
                        <PhoneCall className="w-4 h-4" />
                        {t('admin.notes.typeCall') || 'Telefon'}
                      </div>
                    </SelectItem>
                    <SelectItem value="meeting">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {t('admin.notes.typeMeeting') || 'Toplantı'}
                      </div>
                    </SelectItem>
                    <SelectItem value="complaint">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {t('admin.notes.typeComplaint') || 'Şikayet'}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder={t('admin.notes.placeholder') || 'Görüşme notunu yazın...'}
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                rows={3}
                className="resize-none"
              />
              <div className="flex justify-end">
                <Button 
                  onClick={handleAddNote} 
                  disabled={!newNoteContent.trim() || addingNote}
                  size="sm"
                >
                  {addingNote ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('admin.notes.adding') || 'Ekleniyor...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t('admin.notes.add') || 'Not Ekle'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Notes List */}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : userNotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('admin.notes.empty') || 'Henüz not eklenmemiş.'}</p>
                </div>
              ) : (
                userNotes.map((note) => (
                  <div 
                    key={note.id} 
                    className="p-4 bg-background rounded-lg border hover:border-primary/30 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getNoteTypeBadgeColor(note.note_type)}`}>
                            {getNoteTypeIcon(note.note_type)}
                            {getNoteTypeLabel(note.note_type)}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {note.created_at ? formatDate(note.created_at) : '-'}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t('admin.notes.by') || 'Yazan'}: {note.created_by_name || 'Admin'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={handleClose}>
            {t('common.close') || 'Kapat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailDialog;
